import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { createSeededRandom } from '../utils/random';
import type { Perch } from '../world/WorldBuilder';

/** Body length of a crow (m). */
const CROW_LENGTH = 0.46;
/** She startles crows on the ground from this far (m); ones up on wires let her come closer below. */
const SPOOK_GROUND = 6.5;
const SPOOK_HIGH = 4.5;
/** He startles them from further: they don't like him either. */
const SPOOK_HIM = 8;
/** Seconds a crow flies before it is gone for the night. */
const FLIGHT_TIME = 7;
/** Within this distance (m), sitting crows turn to watch her. */
const WATCH_HER = 16;

function wrapAngle(a: number): number {
  while (a > Math.PI) a -= Math.PI * 2;
  while (a < -Math.PI) a += Math.PI * 2;
  return a;
}

type Crow = {
  perch: Perch;
  root: THREE.Group;
  sitting: THREE.Object3D;
  flyer: THREE.Group;
  wings: THREE.Object3D[];
  state: 'sit' | 'fly' | 'gone';
  t: number;
  vel: THREE.Vector3;
  /** Next idle fidget (turn, hop or peck). */
  fidgetAt: number;
  turnTo: number;
  hop: number;
};

export type BirdSound = (kind: 'flap' | 'caw', x: number, z: number) => void;

/**
 * Crows: perched along the wires and pole crossarms, and picking at the
 * rubbish bags on the ground. They sit still (the odd head turn or hop) and
 * burst away, flapping and cawing, when she or he comes close. Once flown,
 * they are gone until she starts the night again.
 *
 * Sitting crows use a CC0 low-poly bird model (Quaternius), painted black;
 * in flight they swap to a simple silhouette with flapping wings.
 */
export class Birds {
  readonly group = new THREE.Group();
  onSound: BirdSound | null = null;
  private model: THREE.Object3D | null = null;
  private readonly material = new THREE.MeshStandardMaterial({ color: '#141418', roughness: 0.42, metalness: 0.1 });
  private readonly flyerParts: THREE.BufferGeometry[] = [];
  private crows: Crow[] = [];
  private perches: Perch[] = [];
  private clock = 0;
  private nextCaw = 8;
  private readonly rng = createSeededRandom(733);

  constructor() {
    this.group.name = 'crows';
  }

  async load(url: string): Promise<void> {
    const gltf = await new GLTFLoader().loadAsync(url);
    const scene = gltf.scene;
    const box = new THREE.Box3().setFromObject(scene);
    const size = box.getSize(new THREE.Vector3());
    // The model faces +x: scale by its length, sit it on y = 0, turn it to face +z.
    const s = CROW_LENGTH / Math.max(size.x, size.z);
    const centre = box.getCenter(new THREE.Vector3());
    const holder = new THREE.Group();
    scene.position.set(-centre.x, -box.min.y, -centre.z);
    holder.add(scene);
    holder.scale.setScalar(s);
    holder.rotation.y = -Math.PI / 2;
    scene.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      mesh.material = this.material;
      mesh.castShadow = true;
    });
    const wrap = new THREE.Group();
    wrap.add(holder);
    this.model = wrap;
    this.rebuild();
  }

  /** The night's perches (from the world builder). */
  setPerches(perches: Perch[]): void {
    this.perches = perches;
    this.rebuild();
  }

  /** Start of a run: every crow back on its perch. */
  reset(): void {
    for (const c of this.crows) this.settle(c);
    this.nextCaw = 8;
  }

  update(dt: number, her: THREE.Vector3, him: THREE.Vector3 | null, herRunning: boolean): void {
    this.clock += dt;
    for (const c of this.crows) {
      if (c.state === 'gone') continue;
      if (c.state === 'sit') {
        const p = c.perch;
        const dHer = Math.hypot(her.x - p.x, her.z - p.z);
        const dHim = him ? Math.hypot(him.x - p.x, him.z - p.z) : Infinity;
        const spook = (p.ground ? SPOOK_GROUND : SPOOK_HIGH) * (herRunning ? 1.5 : 1);
        if (dHer < spook || dHim < SPOOK_HIM) {
          this.takeOff(c, dHer < dHim ? her : him!);
          continue;
        }
        this.fidget(c, dt, dHer < WATCH_HER ? her : null);
        continue;
      }
      // Flying: climb away, flapping hard at first, then gliding between beats.
      c.t += dt;
      c.vel.y = Math.max(c.vel.y - dt * 1.2, 0.6);
      c.root.position.addScaledVector(c.vel, dt);
      c.root.rotation.y = Math.atan2(c.vel.x, c.vel.z);
      const rate = c.t < 1.2 ? 15 : 9;
      const beat = Math.sin(c.t * rate) * (c.t < 1.2 ? 1 : 0.55 + 0.45 * Math.max(0, Math.sin(c.t * 1.7)));
      c.wings[0].rotation.z = 0.15 + beat * 0.9;
      c.wings[1].rotation.z = -0.15 - beat * 0.9;
      if (c.t > FLIGHT_TIME) {
        c.state = 'gone';
        c.root.visible = false;
      }
    }
    // Now and then a crow somewhere nearby calls.
    if (this.clock > this.nextCaw) {
      this.nextCaw = this.clock + 14 + this.rng() * 22;
      const near = this.crows.filter((c) => c.state === 'sit' && Math.hypot(her.x - c.perch.x, her.z - c.perch.z) < 30);
      const c = near[Math.floor(this.rng() * near.length)];
      if (c) this.onSound?.('caw', c.perch.x, c.perch.z);
    }
  }

  dispose(): void {
    this.clear();
    this.material.dispose();
    for (const g of this.flyerParts) g.dispose();
  }

  private rebuild(): void {
    this.clear();
    if (!this.model) return;
    for (const perch of this.perches) {
      const root = new THREE.Group();
      const sitting = this.model.clone();
      const flyer = this.makeFlyer();
      flyer.visible = false;
      root.add(sitting, flyer);
      this.group.add(root);
      const crow: Crow = {
        perch,
        root,
        sitting,
        flyer,
        wings: flyer.userData.wings as THREE.Object3D[],
        state: 'sit',
        t: 0,
        vel: new THREE.Vector3(),
        fidgetAt: this.rng() * 4,
        turnTo: perch.rot,
        hop: 0,
      };
      this.settle(crow);
      this.crows.push(crow);
    }
  }

  private clear(): void {
    for (const c of this.crows) this.group.remove(c.root);
    this.crows = [];
  }

  private settle(c: Crow): void {
    c.state = 'sit';
    c.t = 0;
    c.hop = 0;
    c.turnTo = c.perch.rot;
    c.root.visible = true;
    c.root.position.set(c.perch.x, c.perch.y, c.perch.z);
    c.root.rotation.set(0, c.perch.rot, 0);
    c.sitting.visible = true;
    c.flyer.visible = false;
  }

  /** Idle life: turn to look about; on the ground, a hop or a peck at the bags. Near her, they turn to watch her go by. */
  private fidget(c: Crow, dt: number, her: THREE.Vector3 | null): void {
    c.fidgetAt -= dt;
    if (her) {
      // Heads turn after her, one crow at a time, and stay on her.
      const at = c.root.rotation.y + wrapAngle(Math.atan2(her.x - c.perch.x, her.z - c.perch.z) - c.root.rotation.y);
      if (c.fidgetAt <= 0 || Math.abs(at - c.turnTo) > 0.35) {
        c.fidgetAt = 0.6 + this.rng() * 1.2;
        c.turnTo = at;
      }
    } else if (c.fidgetAt <= 0) {
      c.fidgetAt = 1.5 + this.rng() * 4;
      c.turnTo = c.root.rotation.y + (this.rng() - 0.5) * (c.perch.ground ? 2.4 : 1.2);
      if (c.perch.ground && this.rng() < 0.5) c.hop = 1;
    }
    c.root.rotation.y += (c.turnTo - c.root.rotation.y) * Math.min(1, dt * 8);
    if (c.hop > 0) {
      c.hop = Math.max(0, c.hop - dt * 4);
      const k = Math.sin(c.hop * Math.PI);
      c.root.position.y = c.perch.y + k * 0.09;
      // Peck: tip forward as it lands.
      c.sitting.rotation.x = (1 - c.hop) * c.hop * 1.4;
    }
  }

  private takeOff(c: Crow, from: THREE.Vector3): void {
    c.state = 'fly';
    c.t = 0;
    c.sitting.visible = false;
    c.flyer.visible = true;
    const away = new THREE.Vector3(c.perch.x - from.x, 0, c.perch.z - from.z);
    if (away.lengthSq() < 0.01) away.set(1, 0, 0);
    away.normalize().applyAxisAngle(new THREE.Vector3(0, 1, 0), (this.rng() - 0.5) * 1.2);
    c.vel.copy(away).multiplyScalar(5.5 + this.rng() * 2.5);
    c.vel.y = c.perch.ground ? 4.2 : 2.6;
    this.onSound?.('flap', c.perch.x, c.perch.z);
    if (this.rng() < 0.55) this.onSound?.('caw', c.perch.x, c.perch.z);
  }

  /** Flying silhouette: body, head, beak, fanned tail and two hinged wings. */
  private makeFlyer(): THREE.Group {
    if (this.flyerParts.length === 0) {
      const body = new THREE.SphereGeometry(0.5, 8, 6);
      const wing = new THREE.BufferGeometry();
      // A swept wing from the shoulder (origin) out along +x, with ragged "fingered" tips.
      wing.setAttribute(
        'position',
        new THREE.Float32BufferAttribute(
          [0, 0, 0.08, 0.42, 0, 0.02, 0, 0, -0.1, 0, 0, -0.1, 0.42, 0, 0.02, 0.4, 0, -0.12, 0.42, 0, 0.02, 0.5, 0, -0.04, 0.4, 0, -0.12],
          3,
        ),
      );
      wing.computeVertexNormals();
      const tail = new THREE.BufferGeometry();
      tail.setAttribute('position', new THREE.Float32BufferAttribute([0, 0, 0, -0.08, 0, -0.18, 0.08, 0, -0.18], 3));
      tail.computeVertexNormals();
      const beak = new THREE.ConeGeometry(0.02, 0.08, 4);
      this.flyerParts.push(body, wing, tail, beak);
    }
    const [body, wing, tail, beak] = this.flyerParts;
    const mat = this.material;
    mat.side = THREE.DoubleSide;
    const g = new THREE.Group();
    const b = new THREE.Mesh(body, mat);
    b.scale.set(0.12, 0.1, 0.34);
    const head = new THREE.Mesh(body, mat);
    head.scale.set(0.085, 0.08, 0.1);
    head.position.set(0, 0.02, 0.19);
    const bk = new THREE.Mesh(beak, mat);
    bk.rotation.x = Math.PI / 2;
    bk.position.set(0, 0.015, 0.27);
    const t = new THREE.Mesh(tail, mat);
    t.position.set(0, 0, -0.14);
    const left = new THREE.Group();
    left.add(new THREE.Mesh(wing, mat));
    left.position.set(0.04, 0.03, 0.02);
    const right = new THREE.Group();
    const rm = new THREE.Mesh(wing, mat);
    rm.scale.x = -1;
    right.add(rm);
    right.position.set(-0.04, 0.03, 0.02);
    g.add(b, head, bk, t, left, right);
    g.userData.wings = [left, right];
    return g;
  }
}
